/**
 * bh-drawer.js — reusable bottom sheet (mobile) / right-side drawer (desktop),
 * built on the native <dialog> element. Behaviour is generic and driven entirely
 * by markup, so any feature can use it without touching this file.
 *
 * Markup contract:
 *   <button aria-controls="MyDrawer">open</button>          ← any trigger, anywhere
 *   <dialog class="bh-sheet" id="MyDrawer">
 *     <div class="bh-sheet__panel">
 *       <button data-bh-sheet-close>×</button>              ← any close control
 *       <span class="bh-sheet__handle"></span>              ← optional: mobile drag-to-dismiss
 *       …content…
 *     </div>
 *   </dialog>
 *
 * showModal() gives us the top layer, backdrop, ESC and focus containment for free;
 * this adds the slide-in, drag-to-dismiss, backdrop-tap close and focus return.
 * Styling (bottom vs right, animation) lives in bh-drawer.css: sheets animate IN and
 * close instantly — no exit animation, so dismissing never feels laggy.
 *
 * Embeds: an <iframe> inside a sheet gets .is-loaded on its load event (CSS fades it in over
 * a placeholder). With `data-bh-prewarm`, a hidden, correctly sized copy of it loads when a
 * trigger for that sheet nears the screen or is touched, so its scripts and tiles are cached
 * before the sheet opens. The copy is thrown away. It can't BE the sheet's iframe: an iframe
 * that loads inside the closed (display:none) dialog initialises at 0×0 and a map centres wrong,
 * and moving a loaded iframe into the sheet reloads it anyway.
 *
 * ceiling: drag starts on the handle only, no velocity fling / snap-points.
 */
(function () {
  const DISMISS = 110; // px dragged down before a sheet closes

  // Background scroll-lock while a sheet is open. Besides stopping the page from scrolling
  // behind the modal, this fixes an iOS Safari bug: a freshly-shown fixed <dialog> is painted
  // at the pre-open scroll offset (the sheet floats mid-screen) until a scroll repaints it.
  // Pinning <body> at the current offset makes the sheet land in the right place on first paint.
  // `top` carries the saved scroll position so there's no visual jump; restored on close.
  let scrollLockY = 0;
  let scrollLocked = false;
  const lockScroll = () => {
    if (scrollLocked) return;
    scrollLocked = true;
    scrollLockY = window.scrollY || window.pageYOffset || 0;
    document.body.style.top = `-${scrollLockY}px`;
    document.body.classList.add('bh-sheet-open');
  };
  const unlockScroll = () => {
    if (!scrollLocked) return;
    scrollLocked = false;
    document.body.classList.remove('bh-sheet-open');
    document.body.style.top = '';
    // Restoring the offset MUST be instant. The theme sets `html { scroll-behavior: smooth }`
    // (app.css), and dropping position:fixed leaves the document at scroll 0 — so a plain
    // scrollTo animates: the page visibly snaps to the top and then rides back down every
    // time a sheet closes. Force `auto` for the duration of the jump only.
    //
    // Not using scrollTo({behavior:'instant'}): that enum value throws on older Safari, which
    // would skip the restore entirely and leave the customer at the top of the page.
    const root = document.documentElement;
    const previousBehavior = root.style.scrollBehavior;
    root.style.scrollBehavior = 'auto';
    void root.offsetHeight; // flush the style change before the scroll reads it
    window.scrollTo(0, scrollLockY);
    root.style.scrollBehavior = previousBehavior;
  };

  function wire(sheet) {
    if (sheet.__bhWired) return;
    sheet.__bhWired = true;

    const panel = sheet.querySelector('.bh-sheet__panel');
    const handle = sheet.querySelector('.bh-sheet__handle');
    let opener = null;
    let startY = null;
    let dy = 0;

    const open = (trigger) => {
      opener = trigger || null;
      // Native [open] + CSS @starting-style (bh-drawer.css) drive the slide-in. No rAF and
      // no class toggle, so every open animates identically — the old double-rAF race is what
      // caused "bounces on first open, then no animation on reopen".
      if (!sheet.open) {
        // THE BOUNCE. A close is held painted for the full duration by the allow-discrete
        // display/overlay transitions in bh-drawer.css. Re-open inside that window and the
        // element is still rendered (display:flex) with the panel mid-slide — measured at
        // y=415 of a 726px travel. Because it never reached display:none, @starting-style
        // does not apply: the panel SNAPS from 415 straight to the closed offset 726, i.e.
        // clean off the bottom of the screen, and only then slides back up. That is the
        // "goes off page and then it comes back" bounce, and it fires on every reopen inside
        // 380ms — which is constantly while anyone is testing the animation.
        //
        // Finishing the in-flight animations lands the element at display:none first, so the
        // next showModal() is a genuine display:none -> rendered change and @starting-style
        // applies. Cheaper and more robust than tracking close state ourselves.
        sheet.getAnimations().forEach((a) => a.finish());
        if (panel) panel.getAnimations().forEach((a) => a.finish());
        lockScroll();
        sheet.showModal();
      }
    };

    // Teardown. Must be idempotent: it runs from close() AND from the dialog's own `close`
    // event, and either one can be the only one that fires.
    const onClosed = () => {
      unlockScroll();
      if (panel) panel.style.transform = '';                          // clear any leftover drag transform
      opener && opener.focus && opener.focus({ preventScroll: true });
    };

    const close = () => {
      // Closing is instant by design — bh-drawer.css declares the slide/fade transitions on
      // the [open] state only, so removing [open] leaves nothing to animate.
      if (!sheet.open) return;
      sheet.close();
      // Do the teardown HERE rather than trusting the `close` event. That event does not
      // reliably fire for these dialogs, and when it doesn't, unlockScroll() never runs —
      // <body> stays position:fixed and the whole page is stuck unscrollable after the
      // customer closes the size guide. On a 95%-mobile store that is fatal, so the one
      // path we control does the work and the event below stays as a backstop.
      onClosed();
    };

    // Expose so the delegated trigger handler can open it, and so other components can close
    // it properly. Always prefer __bhClose over the native sheet.close(): the native call
    // skips the teardown above and leaves <body> scroll-locked.
    sheet.__bhOpen = open;
    sheet.__bhClose = close;

    sheet.addEventListener('click', (e) => { if (e.target === sheet) close(); }); // backdrop tap
    sheet.querySelectorAll('[data-bh-sheet-close]').forEach((b) => b.addEventListener('click', close));
    sheet.addEventListener('cancel', (e) => { e.preventDefault(); close(); });    // ESC → our close()
    sheet.addEventListener('close', onClosed); // backstop for closes that bypass close()

    // Mobile: drag the grab handle down to dismiss.
    if (handle) {
      handle.addEventListener('pointerdown', (e) => {
        startY = e.clientY; dy = 0;
        panel.style.transition = 'none'; // follow the finger 1:1
        handle.setPointerCapture && handle.setPointerCapture(e.pointerId);
      });
      handle.addEventListener('pointermove', (e) => {
        if (startY === null) return;
        dy = Math.max(0, e.clientY - startY);
        panel.style.transform = `translateY(${dy}px)`;
      });
      const release = () => {
        if (startY === null) return;
        const moved = dy; startY = null;
        panel.style.transition = '';   // restore CSS easing
        panel.style.transform = '';    // hand back to CSS: still open → animated snap-back to 0
        if (moved > DISMISS) close();
      };
      handle.addEventListener('pointerup', release);
      handle.addEventListener('pointercancel', release);
    }
  }

  const sheetFor = (trigger) => {
    const t = trigger && document.getElementById(trigger.getAttribute('aria-controls'));
    return t && t.matches && t.matches('dialog.bh-sheet') ? t : null;
  };
  function prewarm(sheet) {
    if (!sheet || sheet.__bhWarm) return;
    sheet.__bhWarm = true;
    sheet.querySelectorAll('iframe[data-bh-prewarm]').forEach((src) => {
      const copy = document.createElement('iframe');
      copy.src = src.getAttribute('src');
      copy.setAttribute('aria-hidden', 'true');
      copy.tabIndex = -1;
      // Real size, off-screen: an embed sized 0×0 doesn't fetch what the visible one will.
      copy.style.cssText = 'position:fixed;left:-10000px;top:0;width:400px;height:360px;border:0;visibility:hidden';
      copy.addEventListener('load', () => setTimeout(() => copy.remove(), 4000), { once: true });
      document.body.appendChild(copy);
    });
  }

  function boot() {
    document.querySelectorAll('dialog.bh-sheet').forEach(wire);

    // Fade embeds in once loaded (the placeholder under them shows until then).
    document.querySelectorAll('dialog.bh-sheet iframe').forEach((f) =>
      f.addEventListener('load', () => f.classList.add('is-loaded'), { once: true })
    );
    // Prewarm: when a trigger nears the viewport, or a finger / focus lands on it.
    const warmable = [...document.querySelectorAll('dialog.bh-sheet')].filter((s) => s.id && s.querySelector('iframe[data-bh-prewarm]'));
    if (warmable.length && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver((entries) => entries.forEach((en) => {
        if (!en.isIntersecting) return;
        prewarm(sheetFor(en.target));
        io.unobserve(en.target);
      }), { rootMargin: '400px 0px' });
      warmable.forEach((s) => document.querySelectorAll(`[aria-controls="${s.id}"]`).forEach((tr) => io.observe(tr)));
    }
    if (warmable.length) ['pointerdown', 'focusin'].forEach((type) => document.addEventListener(type, (e) => {
      const trigger = e.target.closest && e.target.closest('[aria-controls]');
      if (trigger) prewarm(sheetFor(trigger));
    }, { passive: true }));

    // Any [aria-controls] pointing at a .bh-sheet dialog opens it (delegated, so
    // dynamically-added triggers work too). Guarded so it never hijacks unrelated
    // aria-controls (accordions, filters, …).
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[aria-controls]');
      if (!trigger) return;
      const target = document.getElementById(trigger.getAttribute('aria-controls'));
      if (target && target.matches && target.matches('dialog.bh-sheet')) {
        e.preventDefault();
        wire(target); // safe if already wired
        target.__bhOpen(trigger);
      }
    });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
